import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './App.module.css';
import Masthead from './components/Masthead';
import SearchBar from './components/SearchBar';
import ResultsPanel from './components/ResultsPanel';
import ETFResultsPanel from './components/ETFResultsPanel';
import TrendingPanel from './components/TrendingPanel';
import IslamicETFPanel from './components/IslamicETFPanel';
import WatchlistPanel from './components/WatchlistPanel';
import PrintReport from './components/PrintReport';
import PrivacyDialog from './components/PrivacyDialog';
import ErrorBoundary from './components/ErrorBoundary';
import { useWatchlist } from './hooks/useWatchlist';
import ZakatPage from './components/ZakatPage';

const URL_PATTERN   = /^\/(stock|etf)\/([A-Z0-9.\-]{1,15})$/i;
const ZAKAT_PATTERN = /^\/zakat(\/.*)?$/i;

export default function App() {
  const { t } = useTranslation();
  const [loading, setLoading]           = useState(false);
  const [loadingMsg, setLoadingMsg]     = useState('');
  const [error, setError]               = useState('');
  const [result, setResult]             = useState(null);
  const [theme, setTheme]               = useState(() => localStorage.getItem('theme') || 'light');
  const [privacyOpen, setPrivacyOpen]   = useState(false);
  const [activeTicker, setActiveTicker] = useState('');
  const [copied, setCopied]             = useState(false);
  const [view,   setView]               = useState(() =>
    ZAKAT_PATTERN.test(window.location.pathname) ? 'zakat' : 'default'
  );
  const resultsRef                      = useRef(null);
  const copyTimeoutRef                  = useRef(null);
  const watchlist                       = useWatchlist();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setPrivacyOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Auto-screen on initial load if URL contains a permalink
  useEffect(() => {
    const match = window.location.pathname.match(URL_PATTERN);
    if (match) screen(match[2].toUpperCase());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle browser back / forward
  useEffect(() => {
    function handlePopState() {
      const path = window.location.pathname;
      if (ZAKAT_PATTERN.test(path)) {
        setView('zakat');
        return;
      }
      setView('default');
      const match = path.match(URL_PATTERN);
      if (match) {
        screen(match[2].toUpperCase());
      } else {
        setResult(null);
        setError('');
        setActiveTicker('');
      }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const STOCK_MSGS = t('app.loading.stock', { returnObjects: true });

  async function screen(ticker, { depth } = {}) {
    const resolvedDepth = depth || 25;
    setActiveTicker(ticker.toUpperCase().trim());
    setError('');
    setResult(null);
    setLoading(true);

    const isETF = ticker.toUpperCase().includes('ETF');
    const msgs = isETF
      ? [
          t('app.loading.etf_detected'),
          ...Array.from({ length: resolvedDepth }, (_, i) => t('app.loading.etf_screening_holding', { current: i + 1, total: resolvedDepth })),
          t('app.loading.etf_applying_criteria'),
          t('app.loading.etf_computing_compliance'),
          t('app.loading.etf_aggregating'),
          t('app.loading.etf_finalising'),
        ]
      : STOCK_MSGS;

    let msgIdx = 0;
    setLoadingMsg(msgs[0]);
    const interval = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, msgs.length - 1);
      setLoadingMsg(msgs[msgIdx]);
    }, 900);

    try {
      const { data } = await axios.post('/api/screen', { ticker, depth: resolvedDepth });
      setResult(data);

      // Keep watchlist verdict fresh if this ticker is already saved
      if (data.type === 'stock') {
        watchlist.update(ticker.toUpperCase(), data.financials.companyName, data.analysis.overallVerdict, data.financials.price);
      }

      // Push permalink — /stock/AAPL or /etf/QQQ
      const urlType = data.type === 'etf' ? 'etf' : 'stock';
      window.history.pushState({}, '', `/${urlType}/${ticker.toUpperCase()}`);

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setError(msg);
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  }

  function handleHome() {
    setView('default');
    setResult(null);
    setError('');
    window.history.pushState({}, '', '/');
  }

  function handleZakat() {
    setView('zakat');
    window.history.pushState({}, '', '/zakat');
  }

  function handleShare() {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => triggerCopied());
    } else {
      // Fallback for non-HTTPS / older browsers
      const el = document.createElement('textarea');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); triggerCopied(); } catch (_) {}
      document.body.removeChild(el);
    }
  }

  function triggerCopied() {
    setCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  }

  const isETF = result?.type === 'etf';

  return (
    <>
    <PrintReport data={!isETF ? result : null} />
    <div id="app-content" className={styles.wrap}>
      <Masthead
        theme={theme}
        onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
        onPrivacy={() => setPrivacyOpen(true)}
        onHome={handleHome}
        onShare={handleShare}
        onPrint={() => window.print()}
        onZakat={view !== 'zakat' ? handleZakat : null}
        showHome={view === 'zakat'}
        copied={copied}
        hasResult={!!result || !!error}
      />

      {view !== 'zakat' && (
        <SearchBar onScreen={screen} loading={loading} activeTicker={activeTicker} />
      )}

      {view === 'zakat' && (
        <ZakatPage theme={theme} onBack={handleHome} />
      )}

      {view !== 'zakat' && error && (
        <div className={styles.errorBar}>
          <strong>{t('app.error_label')}</strong> {error}
        </div>
      )}

      {view !== 'zakat' && !result && !loading && (
        <>
          <WatchlistPanel
            items={watchlist.items}
            onScreen={screen}
            onRemove={watchlist.remove}
            onClear={watchlist.clear}
            onRefresh={() => window.location.reload()}
          />
          <ErrorBoundary label={t('app.error_boundary.trending')}>
            <TrendingPanel onScreen={screen} />
          </ErrorBoundary>
          <ErrorBoundary label={t('app.error_boundary.etf_list')}>
            <IslamicETFPanel onScreen={screen} />
          </ErrorBoundary>
        </>
      )}

      {view !== 'zakat' && loading && (
        <div className={styles.loadingBar}>
          <div className={styles.spinner} />
          <span>{loadingMsg}</span>
        </div>
      )}

      {view !== 'zakat' && result && (
        <div ref={resultsRef}>
          <ErrorBoundary key={result?.financials?.ticker || result?.etf?.ticker} label={t('app.error_boundary.result')}>
            {isETF
              ? <ETFResultsPanel
                    data={result}
                    theme={theme}
                    onRescreen={(d) => screen(result.etf.ticker, { depth: d })}
                    onScreen={screen}
                    isWatchlisted={watchlist.has(result?.etf?.ticker || '')}
                    onWatchlistToggle={watchlist.toggle}
                  />
              : <ResultsPanel
                    data={result}
                    theme={theme}
                    onScreen={screen}
                    isWatchlisted={watchlist.has(result?.financials?.ticker || '')}
                    onWatchlistToggle={watchlist.toggle}
                  />
            }
          </ErrorBoundary>
        </div>
      )}

      <footer className={styles.footer}>
        {result && (
          <>
            <p>{t('app.footer.data_source')}</p>
            <p>{t('app.footer.analysis')}</p>
            <p>{t('app.footer.aaoifi_thresholds')}</p>
            <p>{t('app.footer.djim_thresholds')}</p>
            <p>{t('app.footer.msci_thresholds')}</p>
            {isETF && <p>{t('app.footer.etf_screening')}</p>}
            <div className={styles.footerDivider} />
          </>
        )}
        <div className={styles.disclaimer}>
          <span className={styles.disclaimerLabel}>{t('app.footer.disclaimer_label')}</span>
          <p>{t('app.footer.disclaimer_text')}</p>
        </div>
        <div className={styles.footerDivider} />
        <p>
          {t('app.footer.built_by')}{' '}
          <a href="https://www.ibst.ca/" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
            {t('app.footer.built_by_company')}
          </a>
          {' '}·{' '}
          <a href="mailto:contact@ibst.ca" className={styles.footerLink}>
            {t('app.footer.contact')}
          </a>
        </p>
      </footer>

      <PrivacyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
    </>
  );
}
